import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCobranzaImpersonation } from '@/contexts/CobranzaImpersonationContext';
import { useCondominioImpersonation } from '@/contexts/CondominioImpersonationContext';
import { usePagePermissions } from '@/hooks/usePagePermissions';
import {
  resolveEffectiveRolId,
  resolveIsSuperAdminView,
  usesTargetRole,
} from '@/lib/impersonation/effective-identity';
import {
  isInFielScope,
  pageFlagKeys,
  resolveFielPagePermissions,
} from '@/lib/impersonation/page-permissions';
import type { EffectiveIdentityInput } from '@/lib/impersonation/types';

/**
 * Identidad efectiva de los portales Cobranza y Productos (comparten el selector
 * "Ver como usuario..." y su contexto `CobranzaImpersonationContext`).
 */
export function useCobranzaEffectiveIdentity(): EffectiveIdentityInput {
  const { profile } = useAuth();
  const { impersonatedEmail, impersonatedName, impersonatedPersonaId, impersonatedRoleId } =
    useCobranzaImpersonation();
  return {
    profileRolId: profile?.rol_id,
    profileRolNombre: profile?.rol_nombre,
    profilePersonaId: profile?.id_persona,
    puedeImpersonar: profile?.puede_impersonar,
    target: impersonatedEmail
      ? {
          email: impersonatedEmail,
          personaId: impersonatedPersonaId,
          nombre: impersonatedName,
          rolId: impersonatedRoleId,
        }
      : null,
  };
}

/** Portal Condominio: su "Ver como" vive en `CondominioImpersonationContext`, no en el de Cobranza. */
export const CONDOMINIO_PAGE_PERMISSION_SCOPE = '/admin/portal-condominio';

/**
 * Identidad efectiva del Portal Condominio. Como en todo el panel, impersonar es
 * siempre vista fiel: rol del impersonado y solo lectura.
 */
function useCondominioEffectiveIdentity(): EffectiveIdentityInput {
  const { profile } = useAuth();
  const { impersonatedEmail, impersonatedName, impersonatedPersonaId, impersonatedRolId } =
    useCondominioImpersonation();
  return {
    profileRolId: profile?.rol_id,
    profileRolNombre: profile?.rol_nombre,
    profilePersonaId: profile?.id_persona,
    puedeImpersonar: profile?.puede_impersonar,
    target: impersonatedEmail
      ? {
          email: impersonatedEmail,
          personaId: impersonatedPersonaId,
          nombre: impersonatedName,
          rolId: impersonatedRolId,
        }
      : null,
  };
}

/** `permisos.nombre` activos de un rol en el submenú de una ruta. */
async function fetchRolePagePermissionNames(pagePath: string, rolId: number): Promise<string[]> {
  const { data: submenu, error: subErr } = await (supabase as any)
    .from('submenus')
    .select('id')
    .eq('vista_front_end', pagePath)
    .eq('activo', true)
    .maybeSingle();
  if (subErr) throw subErr;
  if (!submenu) return [];
  const { data, error } = await (supabase as any)
    .from('submenus_permisos')
    .select('permisos!inner ( nombre )')
    .eq('submenu_id', submenu.id)
    .eq('rol_id', rolId)
    .eq('activo', true);
  if (error) throw error;
  return ((data as any[]) ?? []).map((p) => p.permisos?.nombre).filter(Boolean);
}

/**
 * Permisos de pantalla de los portales Cobranza, Productos y Condominio, con "Vista del usuario".
 *
 *   - En /admin/portal-condominio la impersonación sale de `CondominioImpersonationContext` y
 *     siempre es vista fiel: con "Ver como" activo la pantalla queda en solo lectura.
 *
 *   - Sin impersonar o fuera de esos portales: devuelve EXACTAMENTE lo de
 *     `usePagePermissions(pagePath)` (mismo objeto, mismos flags).
 *   - Impersonando (siempre "Vista del usuario"): los flags salen del rol del IMPERSONADO
 *     (`submenus_permisos`), con toda acción de escritura apagada (edición
 *     bloqueada). `readOnlyView` lo indica y `blockedWriteFlags` dice qué acciones
 *     tendría el usuario y aquí se ocultan.
 *
 * Los flags se toman de las llaves que devuelve `usePagePermissions`: un flag nuevo
 * allá se propaga solo (resuelto con el catálogo de `lib/impersonation/page-permissions`;
 * si no está en el catálogo, en vista fiel queda en `false`).
 *
 * NO toca la autorización de rutas: `useAllowedMenus` / `PermissionRoute` siguen con
 * el rol de la sesión (ver `no-auth-coupling.test.ts`).
 *
 * LÍMITE: las consultas viajan con el token del admin; lo que solo recorte RLS o una
 * RPC (bandejas que filtran por la sesión, 403 de RPC) no se simula.
 */
export function usePortalPagePermissions(pagePath: string) {
  const base = usePagePermissions(pagePath);
  const { pathname } = useLocation();
  const cobranzaIdentity = useCobranzaEffectiveIdentity();
  const condominioIdentity = useCondominioEffectiveIdentity();

  const enCondominio = isInFielScope(pathname, [CONDOMINIO_PAGE_PERMISSION_SCOPE]);
  const identity = enCondominio ? condominioIdentity : cobranzaIdentity;
  // Condominio impersonando sin rol aún resuelto: solo lectura y sin acciones (nunca las del admin).
  const rolPendiente = enCondominio && !!identity.target?.email && identity.target.rolId == null;

  const simulated =
    rolPendiente || ((enCondominio || isInFielScope(pathname)) && usesTargetRole(identity.target));
  const targetIsSuperAdmin = simulated && !rolPendiente && resolveIsSuperAdminView(identity);
  const rolId = simulated && !rolPendiente ? resolveEffectiveRolId(identity) : null;
  const needsQuery = simulated && !targetIsSuperAdmin && rolId != null && !!pagePath;

  const rolPerms = useQuery({
    queryKey: ['permisos-pagina-vista-usuario', pagePath, rolId],
    queryFn: () => fetchRolePagePermissionNames(pagePath, rolId as number),
    enabled: needsQuery,
    staleTime: 60_000,
  });

  return useMemo(() => {
    if (!simulated) {
      return { ...base, readOnlyView: false, blockedWriteFlags: [] as string[] };
    }
    const resolved = resolveFielPagePermissions({
      flagKeys: pageFlagKeys(base),
      permissionNames: new Set(rolPerms.data ?? []),
      targetIsSuperAdmin,
    });
    const cargandoRol = rolPendiente || (needsQuery && rolPerms.data === undefined && !rolPerms.isError);
    return {
      ...base,
      ...(resolved.effective as Partial<typeof base>),
      isLoading: base.isLoading || cargandoRol,
      isSuperAdmin: targetIsSuperAdmin,
      readOnlyView: true,
      blockedWriteFlags: resolved.blockedWriteFlags,
    };
  }, [base, simulated, rolPendiente, targetIsSuperAdmin, needsQuery, rolPerms.data, rolPerms.isError]);
}

/** ¿Está activa la "Vista del usuario" en esta ruta de Cobranza / Productos? */
export function useCobranzaReadOnlyView(): boolean {
  const { pathname } = useLocation();
  const identity = useCobranzaEffectiveIdentity();
  return isInFielScope(pathname) && usesTargetRole(identity.target);
}
